
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Hash, 
  Calendar, 
  CreditCard, 
  Files, 
  CheckCircle2, 
  ArrowRight,
  Zap,
  Play,
  Sun,
  Moon,
  Languages,
  Repeat,
  Target,
  ShieldCheck,
  BarChart3,
  Cloud,
  ChevronRight,
  Sparkles,
  CircleDollarSign,
  Store,
  Users,
  Search,
  BookOpen
} from 'lucide-react';
import { useTranslation } from '../lib/translations';

const Landing: React.FC = () => {
  const [isDark, setIsDark] = useState(document.documentElement.classList.contains('dark'));
  const { t, language, setLanguage } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDark]);

  return (
    <div className="min-h-screen bg-white dark:bg-[#020617] transition-colors duration-300">
      {/* Dynamic Background Blur Glows - Optimized for Mobile rendering */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="blur-bg-glow absolute top-[-15%] left-[-10%] w-[70%] h-[70%] bg-purple-600/10 dark:bg-purple-600/20 blur-[80px] sm:blur-[160px] rounded-full"></div>
        <div className="blur-bg-glow absolute top-[10%] right-[-10%] w-[50%] h-[50%] bg-brand-accent/5 dark:bg-brand-accent/10 blur-[80px] sm:blur-[160px] rounded-full"></div>
        <div className="blur-bg-glow absolute bottom-[-10%] left-[10%] w-[60%] h-[60%] bg-indigo-600/10 dark:bg-indigo-600/20 blur-[80px] sm:blur-[160px] rounded-full"></div>
      </div>

      {/* Navigation */}
      <nav className="fixed top-6 left-1/2 -translate-x-1/2 w-[calc(100%-3rem)] max-w-6xl z-50 glass-card rounded-2xl px-6 h-16 flex items-center justify-between shadow-2xl border-white/40 dark:border-white/5">
        <Link to="/" className="flex items-center gap-3 group">
          <div className="p-2 bg-gradient-to-br from-purple-600 to-indigo-700 rounded-lg shadow-lg group-hover:scale-105 transition-transform">
            <Hash size={18} className="text-white" />
          </div>
          <span className="hidden sm:block text-lg font-black tracking-tight text-slate-900 dark:text-white">SharpNote</span>
        </Link>
        
        <div className="flex items-center gap-4 md:gap-8">
          <Link 
            to="/teachers" 
            className={`flex items-center gap-2 text-[10px] font-black uppercase tracking-widest transition-colors ${location.pathname === '/teachers' ? 'text-purple-600 dark:text-purple-400' : 'text-slate-500 dark:text-slate-400 hover:text-purple-600'}`}
          >
            <Store size={16} />
            {t.nav.marketplace}
          </Link>
          
          <Link 
            to="/pricing" 
            className={`hidden md:flex items-center gap-2 text-[10px] font-black uppercase tracking-widest transition-colors ${location.pathname === '/pricing' ? 'text-purple-600 dark:text-purple-400' : 'text-slate-500 dark:text-slate-400 hover:text-purple-600'}`}
          >
            <CircleDollarSign size={16} />
            {t.nav.pricing}
          </Link>
          
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setLanguage(language === 'en' ? 'fr' : 'en')}
              className="hidden sm:flex items-center gap-2 px-3 py-1.5 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/5 rounded-lg transition-all text-[10px] font-black uppercase tracking-widest"
              aria-label="Toggle Language"
            >
              <Languages size={16} />
              {language.toUpperCase()}
            </button>
            <button 
              onClick={() => setIsDark(!isDark)}
              className="p-2 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/5 rounded-lg transition-all"
              aria-label="Toggle Theme"
            >
              {isDark ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <Link 
              to="/login" 
              className="px-6 py-2 rounded-lg bg-slate-900 dark:bg-white text-white dark:text-slate-950 font-black text-[10px] uppercase tracking-widest hover:scale-105 transition-all shadow-xl"
            >
              {t.landing.enterApp}
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-48 pb-32 px-6">
        <div className="max-w-6xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-1 rounded-lg bg-purple-50 dark:bg-purple-900/10 border border-purple-100 dark:border-purple-500/20 text-purple-600 dark:text-purple-400 text-[10px] font-black uppercase tracking-[0.2em] mb-10 animate-in fade-in slide-in-from-top-4 duration-1000">
            <Sparkles size={12} className="animate-pulse" />
            <span>{t.landing.tagline}</span>
          </div>
          
          <h1 className="text-5xl md:text-7xl lg:text-9xl font-black tracking-tighter text-slate-900 dark:text-white mb-8 leading-[0.85] animate-in fade-in slide-in-from-bottom-8 duration-700">
            {t.landing.heroTitle1} <br />
            <span className="bg-gradient-to-b from-purple-600 via-indigo-600 to-indigo-800 bg-clip-text text-transparent italic">
              {t.landing.heroTitle2}
            </span>
          </h1>
          
          <p className="text-lg md:text-xl text-slate-600 dark:text-slate-400 max-w-3xl mx-auto mb-4 leading-relaxed font-medium">
            {t.landing.heroDesc}
          </p>

          <p className="text-sm md:text-base font-black uppercase tracking-[0.3em] text-purple-600 dark:text-purple-400 mb-12 italic opacity-90 animate-in fade-in zoom-in duration-1000">
            {t.landing.heroSlogan}
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-20">
            <Link 
              to="/login" 
              className="w-full sm:w-auto px-10 py-4 rounded-xl bg-purple-600 text-white font-black text-sm shadow-2xl shadow-purple-500/30 transition-all hover:bg-purple-700 hover:scale-105 active:scale-95 flex items-center justify-center gap-3"
            >
              {t.landing.ctaStart}
              <ArrowRight size={20} />
            </Link>
            <Link 
              to="/teachers"
              className="group w-full sm:w-auto px-10 py-4 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 text-slate-900 dark:text-slate-100 font-black text-sm hover:bg-slate-50 dark:hover:bg-white/5 transition-all flex items-center justify-center gap-3 shadow-xl"
            >
              <Search size={18} className="text-purple-600" />
              {t.landing.ctaMarketplace}
            </Link>
          </div>

          {/* Integration Badge */}
          <div className="flex items-center justify-center gap-8 opacity-40 grayscale hover:grayscale-0 transition-all duration-500">
            <div className="flex items-center gap-2 font-black text-[10px] uppercase tracking-widest text-slate-400">
              <Cloud size={16} /> {t.landing.googleSync}
            </div>
            <div className="flex items-center gap-2 font-black text-[10px] uppercase tracking-widest text-slate-400">
              <Files size={16} /> {t.landing.googleDrive}
            </div>
          </div>
        </div>
      </section>

      {/* Discovery Section */}
      <section className="py-24 px-6 relative bg-slate-50 dark:bg-slate-900/20 border-y border-slate-100 dark:border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1 relative">
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/20 to-teal-600/20 blur-[100px] rounded-full"></div>
              <div className="relative glass-card border-white/20 dark:border-white/5 rounded-3xl overflow-hidden shadow-2xl p-2 aspect-video">
                 <img 
                   src="https://images.unsplash.com/photo-1511379938547-c1f69419868d?auto=format&fit=crop&q=80&w=1200" 
                   alt="Learning Experience" 
                   className="w-full h-full object-cover rounded-2xl grayscale-[0.2] contrast-[1.1]"
                 />
                 <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent"></div>
                 <div className="absolute bottom-6 left-6 right-6">
                    <div className="flex items-center gap-3 bg-white/10 backdrop-blur-xl border border-white/20 p-3 rounded-2xl">
                       <div className="w-10 h-10 rounded-full bg-indigo-600 flex items-center justify-center text-white shadow-lg">
                          <Users size={20} />
                       </div>
                       <div>
                          <p className="text-[10px] font-black uppercase text-white tracking-widest leading-none mb-1">{t.landing.livePortal}</p>
                          <p className="text-white text-xs font-bold leading-none">{t.landing.connectingMentor}</p>
                       </div>
                    </div>
                 </div>
              </div>
            </div>

            <div className="order-1 lg:order-2 space-y-10">
              <div className="space-y-4">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-teal-50 dark:bg-teal-900/10 border border-teal-100 dark:border-teal-500/20 text-teal-600 dark:text-teal-400 text-[10px] font-black uppercase tracking-[0.2em] shadow-sm">
                  {t.pricing.forStudents}
                </div>
                <h2 className="text-4xl md:text-6xl font-black tracking-tighter text-slate-900 dark:text-white leading-none">
                  {t.landing.discoverTitle.split(' ').slice(0, -2).join(' ')} <br /> <span className="text-teal-600">{t.landing.discoverTitle.split(' ').slice(-2).join(' ')}</span>
                </h2>
                <p className="text-slate-500 font-medium text-lg leading-relaxed">
                  {t.landing.discoverDesc}
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                {[
                  { icon: Search, title: t.landing.smartDiscovery, desc: t.landing.smartDiscoveryDesc },
                  { icon: BookOpen, title: t.landing.learningHub, desc: t.landing.learningHubDesc },
                  { icon: Zap, title: t.landing.progressTracker, desc: t.landing.progressTrackerDesc },
                  { icon: ShieldCheck, title: t.landing.secureVerified, desc: t.landing.secureVerifiedDesc }
                ].map((item, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="shrink-0 w-10 h-10 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-white/5 flex items-center justify-center text-teal-600 shadow-sm">
                      <item.icon size={20} />
                    </div>
                    <div>
                      <h4 className="font-black text-sm text-slate-900 dark:text-white uppercase tracking-tight">{item.title}</h4>
                      <p className="text-xs text-slate-500 font-medium">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="pt-4">
                 <Link 
                   to="/teachers" 
                   className="inline-flex items-center gap-3 bg-teal-600 text-white px-8 py-4 rounded-xl font-black text-sm hover:bg-teal-700 transition-all hover:scale-105 shadow-xl shadow-teal-500/20"
                 >
                   {t.landing.ctaMarketplace} <ArrowRight size={20} />
                 </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Workflow Section */}
      <section id="workflow" className="py-24 px-6 relative overflow-hidden">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-12">
              <div className="space-y-4">
                <h2 className="text-4xl md:text-6xl font-black tracking-tighter text-slate-900 dark:text-white leading-none">
                  {t.landing.lifecycleTitle.split('.')[0]} <br /> <span className="text-purple-600">{t.landing.lifecycleTitle.split('.')[1]}</span>
                </h2>
                <p className="text-slate-500 font-medium text-lg italic">{t.landing.lifecycleSubtitle}</p>
              </div>

              <div className="space-y-8">
                {[
                  { 
                    icon: Zap, 
                    title: t.landing.onboardingTitle, 
                    desc: t.landing.onboardingDesc,
                    color: "text-amber-500",
                    bg: "bg-amber-50 dark:bg-amber-500/10"
                  },
                  { 
                    icon: Repeat, 
                    title: t.landing.carryOverTitle, 
                    desc: t.landing.carryOverDesc,
                    color: "text-purple-600",
                    bg: "bg-purple-50 dark:bg-purple-900/10"
                  },
                  { 
                    icon: Target, 
                    title: t.landing.modularTitle, 
                    desc: t.landing.modularDesc,
                    color: "text-teal-600",
                    bg: "bg-teal-50 dark:bg-teal-500/10"
                  }
                ].map((step, i) => (
                  <div key={i} className="flex gap-6 group">
                    <div className={`shrink-0 w-14 h-14 rounded-2xl ${step.bg} ${step.color} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                      <step.icon size={24} />
                    </div>
                    <div>
                      <h3 className="text-xl font-black text-slate-900 dark:text-white mb-2 tracking-tight">{step.title}</h3>
                      <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed font-medium">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-600/20 to-indigo-600/20 blur-[100px] rounded-full animate-pulse"></div>
              <div className="relative glass-card border-white/20 dark:border-white/5 p-8 shadow-2xl rounded-3xl overflow-hidden group">
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-800 animate-pulse"></div>
                    <div className="space-y-1">
                      <div className="h-3 w-24 bg-slate-100 dark:bg-slate-800 rounded animate-pulse"></div>
                      <div className="h-2 w-16 bg-slate-50 dark:bg-slate-900 rounded animate-pulse"></div>
                    </div>
                  </div>
                  <div className="h-6 w-20 bg-purple-600/20 rounded-full border border-purple-500/30"></div>
                </div>
                
                <div className="space-y-4 mb-8">
                  <div className="p-4 bg-slate-50 dark:bg-slate-950/50 rounded-xl space-y-3">
                    <div className="flex items-center gap-2 text-[9px] font-black uppercase tracking-widest text-slate-400">
                      <Target size={12} className="text-purple-600" /> {t.dashboard.performanceNotes}
                    </div>
                    <div className="space-y-2">
                      <div className="h-2 w-full bg-purple-600/10 rounded"></div>
                      <div className="h-2 w-[80%] bg-purple-600/10 rounded"></div>
                    </div>
                  </div>
                  <div className="p-4 bg-slate-50 dark:bg-slate-950/50 rounded-xl space-y-3">
                    <div className="flex items-center gap-2 text-[9px] font-black uppercase tracking-widest text-slate-400">
                      <Files size={12} className="text-teal-600" /> {t.programs.assetsTitle}
                    </div>
                    <div className="flex gap-2">
                      <div className="h-8 w-8 bg-white dark:bg-slate-900 rounded border border-slate-200 dark:border-white/5"></div>
                      <div className="h-8 w-8 bg-white dark:bg-slate-900 rounded border border-slate-200 dark:border-white/5"></div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end">
                   <div className="px-6 py-2.5 bg-purple-600 text-white text-[10px] font-black uppercase tracking-widest rounded-lg shadow-lg shadow-purple-900/40">
                      {t.studentDetail.modals.loggedTitle}
                   </div>
                </div>
                
                {/* Decorative Elements */}
                <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-brand-accent/20 blur-[60px] rounded-full"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Pillars Grid */}
      <section id="features" className="py-24 px-6 relative bg-slate-50/50 dark:bg-transparent border-y border-slate-100 dark:border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-3xl md:text-5xl font-black mb-4 tracking-tight text-slate-900 dark:text-white">{t.landing.featuresTitle}</h2>
            <p className="text-slate-500 dark:text-slate-500 text-base font-medium max-w-2xl mx-auto italic">{t.landing.featuresDesc}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Calendar,
                title: t.landing.precisionScheduling,
                desc: t.landing.schedulingDesc,
                color: "text-purple-600 dark:text-purple-400",
                bg: "bg-purple-50 dark:bg-purple-500/10",
                footer: t.landing.googleSync
              },
              {
                icon: CreditCard,
                title: t.landing.financialTitle,
                desc: t.landing.financialDesc,
                color: "text-teal-600 dark:text-teal-400",
                bg: "bg-teal-50 dark:bg-teal-500/10",
                footer: t.profile.creditPacksTitle
              },
              {
                icon: BarChart3,
                title: t.landing.growthTitle,
                desc: t.landing.growthDesc,
                color: "text-indigo-600 dark:text-indigo-400",
                bg: "bg-indigo-50 dark:bg-indigo-900/10",
                footer: t.nav.insights
              }
            ].map((f, i) => (
              <div key={i} className="group bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 rounded-2xl p-8 hover:border-purple-500/30 transition-all duration-300 shadow-sm hover:shadow-2xl flex flex-col">
                <div className={`p-4 rounded-xl ${f.bg} ${f.color} w-fit mb-8 transition-transform duration-500 group-hover:rotate-12 border border-transparent dark:border-white/5 shadow-inner`}>
                  <f.icon size={32} />
                </div>
                <h3 className="text-2xl font-black mb-4 text-slate-900 dark:text-white tracking-tight leading-tight">{f.title}</h3>
                <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed font-medium mb-8 flex-1">{f.desc}</p>
                <div className="pt-6 border-t border-slate-100 dark:border-white/5 flex items-center justify-between">
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">{f.footer}</span>
                  <ChevronRight size={14} className="text-slate-300 group-hover:text-purple-600 transition-colors" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust & Stats Section */}
      <section className="py-24 px-6 relative">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 rounded-3xl p-8 lg:p-20 overflow-hidden relative shadow-2xl">
            <div className="relative z-10 flex flex-col lg:flex-row items-center gap-16">
              <div className="flex-1 space-y-10 text-left">
                <h2 className="text-4xl md:text-7xl font-black tracking-tighter leading-[0.9] text-slate-900 dark:text-white">
                  {t.landing.unpluggedTitle.split(',')[0]}, <br />
                  <span className="text-purple-600 italic">{t.landing.unpluggedTitle.split(',')[1].trim()}</span>
                </h2>
                <div className="space-y-6">
                  {[
                    t.landing.inviteMagic,
                    t.landing.efficiencySub,
                    t.landing.curriculumSub,
                    t.landing.secureArchitecture
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-4 text-slate-700 dark:text-slate-300 font-bold text-lg">
                      <div className="p-1 bg-teal-100 dark:bg-teal-500/10 rounded-full border border-teal-200 dark:border-teal-500/20">
                        <CheckCircle2 className="text-teal-600 dark:text-teal-400" size={20} />
                      </div>
                      {item}
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-6 w-full">
                <div className="bg-slate-50 dark:bg-slate-950 p-6 md:p-10 rounded-2xl border border-slate-200 dark:border-white/5 text-center flex flex-col justify-center shadow-inner group hover:border-purple-500/30 transition-all">
                  <span className="text-4xl sm:text-5xl md:text-6xl font-black text-slate-900 dark:text-white mb-2 tracking-tighter group-hover:scale-110 transition-transform">98%</span>
                  <span className="text-[9px] text-slate-500 uppercase font-black tracking-[0.2em]">{t.landing.efficiency}</span>
                </div>
                <div className="bg-purple-600 p-6 md:p-10 rounded-2xl text-center flex flex-col justify-center shadow-2xl shadow-purple-900/40 md:translate-y-10 group hover:scale-[1.02] transition-all">
                  <span className="text-4xl sm:text-5xl md:text-6xl font-black text-white mb-2 tracking-tighter">Zero</span>
                  <span className="text-[9px] text-purple-200 uppercase font-black tracking-[0.2em]">{t.landing.latePayments}</span>
                </div>
              </div>
            </div>
            
            {/* Background Accent */}
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-600/5 blur-[100px] rounded-full -mr-20 -mb-20"></div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <footer className="py-32 px-6 text-center border-t border-slate-100 dark:border-white/5 relative overflow-hidden">
        <div className="max-w-4xl mx-auto space-y-12 relative z-10">
          <h2 className="text-5xl md:text-8xl font-black text-slate-900 dark:text-white tracking-tighter leading-none">{t.landing.readyToOrchestrate}</h2>
          <p className="text-slate-500 text-lg font-medium max-w-xl mx-auto">{t.landing.readyDesc}</p>
          <Link 
            to="/login" 
            className="inline-flex items-center gap-4 bg-slate-900 dark:bg-white text-white dark:text-slate-950 px-12 py-5 rounded-2xl font-black text-xl hover:scale-105 transition-all shadow-2xl hover:shadow-purple-500/20"
          >
            {t.landing.launch}
            <ArrowRight size={28} />
          </Link>
          
          <div className="pt-24 flex flex-col items-center gap-8">
            <Link to="/" className="flex items-center gap-3 group">
              <div className="p-2.5 bg-slate-100 dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-white/5 group-hover:scale-110 transition-transform">
                <Hash size={20} className="text-purple-600" />
              </div>
              <span className="font-black text-slate-400 dark:text-slate-500 text-xs tracking-tight uppercase tracking-[0.3em] group-hover:text-slate-900 dark:group-hover:text-white transition-colors">SharpNote &copy; 2026</span>
            </Link>
            
            <div className="flex items-center gap-8 text-[10px] font-black uppercase tracking-widest text-slate-400">
               <Link to="/privacy" className="hover:text-purple-600 transition-colors">{t.landing.privacy}</Link>
               <Link to="/terms" className="hover:text-purple-600 transition-colors">{t.landing.terms}</Link>
               <Link to="/support" className="hover:text-purple-600 transition-colors">{t.landing.footerSupport}</Link>
            </div>
          </div>
        </div>
        
        {/* Decorative Background for footer */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-purple-600/5 blur-[150px] rounded-full pointer-events-none"></div>
      </footer>
    </div>
  );
};

export default Landing;
